import { useEffect, useMemo, useRef } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import type { ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import type { ConnectProto } from "@racoon/api";
import type { RootState } from "~/shared/store/store";
import { contractGrpc } from "~/shared/api/contract/contract.grpc";
import { contractsByCounterparty } from "./counterparty-contract";

type ContractsArg = Parameters<typeof contractGrpc.endpoints.listContracts.initiate>[0];
/** The store as the endpoint's selectors type it (they don't know the app's RootState). */
type ApiState = Parameters<ReturnType<typeof contractGrpc.endpoints.listContracts.select>>[0];

/**
 * The «Договор» cell's contract for each loaded counterparty, by organization id: their
 * contracts with `providerId` and its children (the server expands the provider to its
 * descendants). One request per `chunkSize` ids, in load order, so a newly loaded page
 * fetches only its own rows' contracts and the earlier pages stay cached. Without a
 * `list_filter`, ListContracts returns every match: no page of contracts is cut off.
 */
export const useCounterpartyContracts = (
  providerId: number | string | null | undefined,
  counterpartyIds: readonly bigint[],
  chunkSize: number,
): Map<string, ConnectProto.Contract> => {
  const dispatch = useDispatch<ThunkDispatch<RootState, unknown, UnknownAction>>();
  const idsKey = counterpartyIds.join(",");
  const args = useMemo<ContractsArg[]>(() => {
    if (!providerId || !idsKey) return [];
    const ids = idsKey.split(",").map(BigInt);
    const chunks: ContractsArg[] = [];
    for (let start = 0; start < ids.length; start += chunkSize) {
      chunks.push({
        serviceProviderIds: [BigInt(providerId)],
        targetOrganizationIds: ids.slice(start, start + chunkSize),
      });
    }
    return chunks;
  }, [providerId, idsKey, chunkSize]);

  // Re-subscribing an unchanged chunk reads the cache: an unused entry outlives the
  // effect's cleanup, so only the new page goes to the network.
  useEffect(() => {
    const subscriptions = args.map((arg) =>
      dispatch(contractGrpc.endpoints.listContracts.initiate(arg)),
    );
    return () => subscriptions.forEach((subscription) => subscription.unsubscribe());
  }, [dispatch, args]);

  const pages = useSelector(
    (state: ApiState) =>
      args.map((arg) => contractGrpc.endpoints.listContracts.select(arg)(state).data),
    shallowEqual,
  );

  // A chunk still loading (a sort or a refresh re-cut the ids) keeps its rows' last
  // contracts rather than flashing «—». A loaded chunk is the truth, so a contract that is
  // gone does disappear.
  const previous = useRef(new Map<string, ConnectProto.Contract>());
  const picked = useMemo(() => {
    const fresh = contractsByCounterparty(pages.flatMap((page) => page?.contracts ?? []));
    args.forEach((arg, index) => {
      if (pages[index]) return;
      for (const id of arg.targetOrganizationIds ?? []) {
        const kept = previous.current.get(String(id));
        if (kept) fresh.set(String(id), kept);
      }
    });
    return fresh;
  }, [args, pages]);
  useEffect(() => {
    previous.current = picked;
  }, [picked]);
  return picked;
};
